// src/components/TeamLeadDashboard.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Button from '../../components/Button';
import Logout from '../../components/Logout';
import './styles.css';

function TeamLeadDashboard() {
  const [chemicals, setChemicals] = useState([]);
  const [filteredChemicals, setFilteredChemicals] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState('');
  const [notification, setNotification] = useState(null);

  const [newChemicalName, setNewChemicalName] = useState('');
  const [chemicalInputError, setChemicalInputError] = useState('');

  const [selectedChemical, setSelectedChemical] = useState(null);
  const [drums, setDrums] = useState([]);
  const [newDrumWeight, setNewDrumWeight] = useState('');
  const [newLotNumber, setNewLotNumber] = useState('');
  const [containerType, setContainerType] = useState('Drum');

  const [selectedThreshold, setSelectedThreshold] = useState('');
  const [locationInput, setLocationInput] = useState('');

  const [isLoading, setIsLoading] = useState(false);
  const [newWeights, setNewWeights] = useState({});
  const [successMessages, setSuccessMessages] = useState({});
  const [loadingDrumId, setLoadingDrumId] = useState(null);

  // Incoming inventory state
  const [incomingDrums, setIncomingDrums] = useState([]);
  const [incomingChemicalSearch, setIncomingChemicalSearch] = useState('');
  const [newIncomingWeight, setNewIncomingWeight] = useState('');
  const [newIncomingTracking, setNewIncomingTracking] = useState('');
  const [incomingChemicalId, setIncomingChemicalId] = useState('');

  // Logged-in user
  const [user, setUser] = useState(null);

  // Activity log
  const [activityLog, setActivityLog] = useState([]);

  const logActivity = (message) => {
    setActivityLog(prevLog => [
      ...prevLog,
      { message, timestamp: new Date().toLocaleTimeString() }
    ]);
  };

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });

    setTimeout(() => {
      setNotification(null);
    }, 4000);
  };

  const incomingChemicalMatches = chemicals
    .filter(c =>
      incomingChemicalSearch.trim() !== '' &&
      (
        c.name.toLowerCase().includes(incomingChemicalSearch.toLowerCase()) ||
        (c.location || '').toLowerCase().includes(incomingChemicalSearch.toLowerCase())
      )
    )
    .slice(0, 8);

    const dashboardStats = {
      chemicalCount: chemicals.length,
    
      inventoryItemCount: drums.length,
    
      incomingItemCount: incomingDrums.length,
    
      lowStockCount: chemicals.filter(c =>
        c.threshold !== null &&
        c.threshold !== undefined &&
        c.total_weight < c.threshold
      ).length
    };
    
    const exportInventoryToCSV = () => {
      const headers = [
        'Chemical Code',
        'Location',
        'Total Weight',
        'Threshold'
      ];
    
      const rows = chemicals.map(chemical => [
        chemical.name,
        chemical.location || '',
        chemical.total_weight ?? 0,
        chemical.threshold ?? ''
      ]);
    
      const csvContent = [headers, ...rows]
        .map(row =>
          row.map(value => `"${String(value).replace(/"/g, '""')}"`).join(',')
        )
        .join('\n');
    
      const blob = new Blob([csvContent], {
        type: 'text/csv;charset=utf-8;'
      });
    
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
    
      link.href = url;
      link.setAttribute('download', 'chemical-inventory.csv');
    
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    
      URL.revokeObjectURL(url);
    
      showNotification('Inventory export downloaded.', 'success');
    };
    const downloadCSV = (filename, headers, rows) => {
      const csvContent = [headers, ...rows]
        .map(row =>
          row.map(value => `"${String(value ?? '').replace(/"/g, '""')}"`).join(',')
        )
        .join('\n');
    
      const blob = new Blob([csvContent], {
        type: 'text/csv;charset=utf-8;'
      });
    
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
    
      link.href = url;
      link.setAttribute('download', filename);
    
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    
      URL.revokeObjectURL(url);
    };
    
    const exportAllInventoryCSV = () => {
      downloadCSV(
        'all-chemical-inventory.csv',
        ['Chemical Code', 'Location', 'Total Weight', 'Threshold'],
        chemicals.map(chemical => [
          chemical.name,
          chemical.location || '',
          chemical.total_weight ?? 0,
          chemical.threshold ?? ''
        ])
      );
    
      showNotification('All inventory export downloaded.', 'success');
    };
    
    const exportLowStockCSV = () => {
      const lowStock = chemicals.filter(c =>
        c.threshold !== null &&
        c.threshold !== undefined &&
        c.total_weight < c.threshold
      );
    
      downloadCSV(
        'low-stock-chemical-inventory.csv',
        ['Chemical Code', 'Location', 'Total Weight', 'Threshold', 'Amount Below Threshold'],
        lowStock.map(chemical => [
          chemical.name,
          chemical.location || '',
          chemical.total_weight ?? 0,
          chemical.threshold ?? '',
          chemical.threshold - chemical.total_weight
        ])
      );
    
      showNotification('Low stock export downloaded.', 'success');
    };
    
    const exportIncomingInventoryCSV = () => {
      downloadCSV(
        'incoming-chemical-inventory.csv',
        ['Chemical Code', 'Weight', 'Tracking Number', 'Arrival Date'],
        incomingDrums.map(item => [
          item.chemical_name || '',
          item.weight ?? 0,
          item.tracking_number || '',
          item.arrival_date || ''
        ])
      );
    
      showNotification('Incoming inventory export downloaded.', 'success');
    };
    useEffect(() => {
      try {
        const storedUser = JSON.parse(sessionStorage.getItem('user'));
        console.log('👤 Loaded User from Session:', storedUser);
        setUser(storedUser);
      } catch (err) {
        console.error('Could not load user from sessionStorage:', err);
        setUser(null);
      }
    }, []);
    
    useEffect(() => {
      console.log('🏁 Fetching Chemicals on Mount...');
      fetchChemicals().then(() => {
        setSelectedChemical(null);
      });
    
      fetchIncomingDrums();
    }, []);
    
    useEffect(() => {
      if (selectedChemical) {
        fetchDrums(selectedChemical);
        setLocationInput(selectedChemical.location || '');
      } else {
        setDrums([]);
        setLocationInput('');
      }
    }, [selectedChemical]);
    
    useEffect(() => {
      const isLowStock = (chemical) =>
        chemical.threshold !== null &&
        chemical.threshold !== undefined &&
        chemical.total_weight < chemical.threshold;
    
      const sortLowStockFirst = (list) => {
        return [...list].sort((a, b) => {
          const aLow = isLowStock(a);
          const bLow = isLowStock(b);
    
          if (aLow && !bLow) return -1;
          if (!aLow && bLow) return 1;
    
          return a.name.localeCompare(b.name);
        });
      };
    
      const term = searchTerm.trim().toLowerCase();
    
      if (!term) {
        setFilteredChemicals(sortLowStockFirst(chemicals));
        return;
      }
    
      const filtered = chemicals.filter(c =>
        c.name.toLowerCase().includes(term)
      );
    
      setFilteredChemicals(sortLowStockFirst(filtered));
    }, [searchTerm, chemicals]);
    
    const fetchChemicals = async () => {
      setIsLoading(true);
      setError('');
    
      try {
        console.log('Fetching chemicals...');
    
        const response = await fetch('https://crtapp.xyz/api/inventory', {
          cache: 'no-store',
          credentials: 'include'
        });
    
        console.log('Fetch response status:', response.status);
    
        if (!response.ok) {
          throw new Error('Failed to load chemicals');
        }
    
        const data = await response.json();
        console.log('Fetched chemicals:', data);
    
        setChemicals(data);
      } catch (err) {
        console.error('Fetch error:', err);
        setError('Could not load chemicals. Please try again.');
        showNotification('Could not load chemical inventory.', 'error');
      } finally {
        setIsLoading(false);
      }
    };
    
    const fetchDrums = async (chemical) => {
      if (!chemical?.id) {
        console.error('No chemical selected for fetching inventory items.');
        return;
      }
    
      try {
        const response = await fetch(
          `https://crtapp.xyz/api/chemicals/${chemical.id}/drums`,
          { credentials: 'include' }
        );
    
        if (!response.ok) {
          throw new Error(`Failed to fetch inventory items for ${chemical.name}`);
        }
    
        const data = await response.json();
        setDrums(data);
      } catch (err) {
        console.error('Error fetching inventory items:', err);
        showNotification('Could not load inventory items.', 'error');
      }
    };

  const updateThresholdForChemical = async (chemicalId, thresholdValue) => {
    if (!chemicalId) {
      showNotification('Please select a chemical code first.', 'error');
      return;
    }

    const parsedThreshold = parseFloat(thresholdValue);

    if (Number.isNaN(parsedThreshold) || parsedThreshold < 0) {
      showNotification('Please enter a valid threshold weight.', 'error');
      return;
    }

    try {
      const response = await fetch(
        `https://crtapp.xyz/api/chemicals/${chemicalId}/threshold`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ threshold: parsedThreshold }),
          credentials: 'include'
        }
      );

      if (!response.ok) {
        const data = await response.json();
        showNotification(data.message || 'Failed to update threshold.', 'error');
        return;
      }

      setSelectedChemical(prev =>
        prev ? { ...prev, threshold: parsedThreshold } : prev
      );

      setChemicals(prevChemicals =>
        prevChemicals.map(c =>
          c.id === chemicalId ? { ...c, threshold: parsedThreshold } : c
        )
      );

      await fetchChemicals();
      setSelectedThreshold('');

      showNotification('Threshold updated successfully.', 'success');
      logActivity(`Updated threshold for ${selectedChemical?.name || chemicalId} to ${parsedThreshold} lbs.`);
    } catch (err) {
      console.error('Error updating threshold:', err);
      showNotification('Error updating threshold. Please try again.', 'error');
    }
  };

  const updateLocationForChemical = async (chemicalId, locationValue) => {
    if (!chemicalId) {
      showNotification('Please select a chemical code first.', 'error');
      return;
    }

    try {
      const response = await fetch(
        `https://crtapp.xyz/api/chemicals/${chemicalId}/location`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ location: locationValue }),
          credentials: 'include'
        }
      );

      if (!response.ok) {
        const data = await response.json();
        showNotification(data.message || 'Failed to update location.', 'error');
        return;
      }

      setSelectedChemical(prev =>
        prev ? { ...prev, location: locationValue } : prev
      );

      setChemicals(prevChemicals =>
        prevChemicals.map(c =>
          c.id === chemicalId ? { ...c, location: locationValue } : c
        )
      );

      setFilteredChemicals(prevChemicals =>
        prevChemicals.map(c =>
          c.id === chemicalId ? { ...c, location: locationValue } : c
        )
      );

      await fetchChemicals();

      showNotification('Location updated successfully.', 'success');
      logActivity(
        `Updated location for ${selectedChemical?.name || chemicalId} to ${locationValue || 'Not assigned'}.`
      );
    } catch (err) {
      console.error('Error updating location:', err);
      showNotification('Error updating location. Please try again.', 'error');
    }
  };

  const fetchIncomingDrums = async () => {
    try {
      const response = await fetch('https://crtapp.xyz/api/incoming-drums', {
        cache: 'no-store',
        credentials: 'include'
      });
  
      if (!response.ok) {
        throw new Error('Failed to load incoming drums');
      }
  
      const data = await response.json();
      setIncomingDrums(data);
    } catch (err) {
      console.error('Error fetching incoming drums:', err);
      setError('Could not load incoming drums. Please try again.');
      showNotification('Could not load incoming drums.', 'error');
    }
  };
  
  const handleAddChemical = async () => {
    const chemicalCode = newChemicalName.trim();
  
    if (!chemicalCode) {
      setChemicalInputError('Chemical code is required.');
      showNotification('Chemical code is required.', 'error');
      return;
    }
  
    try {
      const response = await fetch('https://crtapp.xyz/api/chemicals', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: chemicalCode }),
        credentials: 'include'
      });
  
      if (!response.ok) {
        let message = 'Failed to add chemical code.';
  
        try {
          const data = await response.json();
          message = data.message || message;
        } catch {
          message = `Failed to add chemical code. Server returned ${response.status}.`;
        }
  
        showNotification(message, 'error');
        return;
      }
  
      setNewChemicalName('');
      setChemicalInputError('');
  
      await fetchChemicals();
  
      showNotification(`${chemicalCode} added successfully.`, 'success');
      logActivity(`Added chemical code: ${chemicalCode}`);
    } catch (err) {
      console.error('Error adding chemical:', err);
      showNotification('Could not add chemical code. Please try again.', 'error');
    }
  };
  
  const handleDeleteChemical = async (chemicalIdToDelete) => {
    if (!chemicalIdToDelete) {
      showNotification('Please select a chemical code to delete.', 'error');
      return;
    }
  
    const chemicalToDelete = chemicals.find(
      c => c.id === parseInt(chemicalIdToDelete)
    );
  
    const confirmed = window.confirm(
      `Are you sure you want to delete ${
        chemicalToDelete ? chemicalToDelete.name : 'this chemical code'
      }?`
    );
  
    if (!confirmed) return;
  
    try {
      console.log('Deleting chemical ID:', chemicalIdToDelete);
  
      const response = await fetch(
        `https://crtapp.xyz/api/chemicals/${chemicalIdToDelete}`,
        {
          method: 'DELETE',
          credentials: 'include'
        }
      );
  
      if (!response.ok) {
        let message = 'Failed to delete chemical code.';
  
        try {
          const data = await response.json();
          message = data.message || message;
        } catch {
          message = `Delete failed. Server returned ${response.status}.`;
        }
  
        console.error('Delete failed:', message);
        showNotification(message, 'error');
        return;
      }
  
      await fetchChemicals();
  
      showNotification(
        `${chemicalToDelete ? chemicalToDelete.name : chemicalIdToDelete} deleted successfully.`,
        'success'
      );
  
      logActivity(
        `Deleted chemical code: ${
          chemicalToDelete ? chemicalToDelete.name : chemicalIdToDelete
        }`
      );
  
      if (selectedChemical?.id === parseInt(chemicalIdToDelete)) {
        setSelectedChemical(null);
        setDrums([]);
      }
    } catch (err) {
      console.error('Error deleting chemical:', err);
      showNotification('Error deleting chemical code. Please try again.', 'error');
    }
  };
  const handleAddDrum = async () => {
    if (!selectedChemical?.id) {
      showNotification('Please select a chemical code first.', 'error');
      return;
    }
  
    const parsedWeight = parseFloat(newDrumWeight);
  
    if (!newDrumWeight || Number.isNaN(parsedWeight) || parsedWeight <= 0) {
      showNotification('Please enter a valid inventory item weight.', 'error');
      return;
    }
  
    try {
      const response = await fetch(
        `https://crtapp.xyz/api/chemicals/${selectedChemical.id}/drums`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            weight: parsedWeight,
            lot_number: newLotNumber.trim(),
            container_type: containerType
          }),
          credentials: 'include'
        }
      );
  
      if (!response.ok) {
        let message = 'Failed to add inventory item.';
  
        try {
          const data = await response.json();
          message = data.message || message;
        } catch {
          message = `Failed to add inventory item. Server returned ${response.status}.`;
        }
  
        showNotification(message, 'error');
        return;
      }
  
      const addedWeight = parsedWeight;
      const addedLot = newLotNumber.trim() || 'N/A';
      const addedType = containerType;
  
      setNewDrumWeight('');
      setNewLotNumber('');
      setContainerType('Drum');
  
      await fetchChemicals();
      await fetchDrums(selectedChemical);
  
      showNotification(`${addedType} added successfully.`, 'success');
  
      logActivity(
        `Added ${addedType} of ${addedWeight} lbs to ${selectedChemical.name} (Lot: ${addedLot})`
      );
    } catch (err) {
      console.error('Error adding inventory item:', err);
      showNotification('Could not add inventory item. Please try again.', 'error');
    }
  };
  
  const handleEditWeight = async (drumId, newWeight) => {
    const parsedWeight = parseFloat(newWeight);
  
    if (!newWeight || Number.isNaN(parsedWeight) || parsedWeight < 0) {
      showNotification('Please enter a valid weight.', 'error');
      return;
    }
  
    setLoadingDrumId(drumId);
    setSuccessMessages(prev => ({ ...prev, [drumId]: null }));
  
    try {
      const response = await fetch(
        `https://crtapp.xyz/api/drums/${drumId}/update-weight`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ new_weight: parsedWeight })
        }
      );
  
      if (!response.ok) {
        let message = 'Failed to update weight.';
  
        try {
          const data = await response.json();
          message = data.message || message;
        } catch {
          message = `Failed to update weight. Server returned ${response.status}.`;
        }
  
        showNotification(message, 'error');
        return;
      }
  
      setDrums(prevDrums =>
        prevDrums.map(drum =>
          drum.id === drumId ? { ...drum, weight: parsedWeight } : drum
        )
      );
  
      setNewWeights(prev => ({
        ...prev,
        [drumId]: ''
      }));
  
      await fetchChemicals();
  
      setSuccessMessages(prev => ({
        ...prev,
        [drumId]: `✅ Updated to ${parsedWeight} lbs!`
      }));
  
      showNotification('Weight updated successfully.', 'success');
  
      setTimeout(() => {
        setSuccessMessages(prev => ({ ...prev, [drumId]: null }));
      }, 3000);
    } catch (err) {
      console.error('Error updating inventory item weight:', err);
      showNotification('An error occurred while updating weight.', 'error');
    } finally {
      setLoadingDrumId(null);
    }
  };
  
  const handleDeleteDrum = async (drumId) => {
    const drumToDelete = drums.find(d => d.id === drumId);
  
    const confirmed = window.confirm(
      `Are you sure you want to delete this ${
        drumToDelete?.container_type || 'inventory item'
      }?`
    );
  
    if (!confirmed) return;
  
    try {
      const response = await fetch(`https://crtapp.xyz/api/drums/${drumId}`, {
        method: 'DELETE',
        cache: 'no-store',
        credentials: 'include'
      });
  
      if (!response.ok) {
        let message = 'Failed to delete inventory item.';
  
        try {
          const data = await response.json();
          message = data.message || message;
        } catch {
          message = `Failed to delete inventory item. Server returned ${response.status}.`;
        }
  
        showNotification(message, 'error');
        return;
      }
  
      await fetchDrums(selectedChemical);
      await fetchChemicals();
  
      showNotification('Inventory item deleted successfully.', 'success');
  
      logActivity(
        `Deleted ${
          drumToDelete?.container_type || 'inventory item'
        } ${
          drumToDelete
            ? (drumToDelete.lot_number ? `(Lot: ${drumToDelete.lot_number})` : `ID: ${drumId}`)
            : drumId
        } from ${selectedChemical ? selectedChemical.name : 'selected chemical'}`
      );
    } catch (err) {
      console.error('Error deleting inventory item:', err);
      showNotification('Could not delete inventory item. Please try again.', 'error');
    }
  };
  
  const handleAddIncomingDrum = async () => {
    if (!incomingChemicalId) {
      showNotification('Please select a chemical code for the incoming item.', 'error');
      return;
    }
  
    const parsedWeight = parseFloat(newIncomingWeight);
  
    if (!newIncomingWeight || Number.isNaN(parsedWeight) || parsedWeight <= 0) {
      showNotification('Please enter a valid incoming weight.', 'error');
      return;
    }
  
    try {
      const response = await fetch('https://crtapp.xyz/api/incoming-drums', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          weight: parsedWeight,
          chemical_id: parseInt(incomingChemicalId),
          tracking_number: newIncomingTracking.trim()
        }),
        credentials: 'include'
      });
  
      if (!response.ok) {
        let message = 'Failed to add incoming item.';
  
        try {
          const data = await response.json();
          message = data.message || message;
        } catch {
          message = `Failed to add incoming item. Server returned ${response.status}.`;
        }
  
        showNotification(message, 'error');
        return;
      }
  
      const chemicalForIncoming = chemicals.find(
        c => c.id === parseInt(incomingChemicalId)
      );
  
      setNewIncomingWeight('');
      setNewIncomingTracking('');
      setIncomingChemicalId('');
      setIncomingChemicalSearch('');
  
      await fetchIncomingDrums();
  
      showNotification('Incoming item added successfully.', 'success');
  
      logActivity(
        `Added incoming item of ${parsedWeight} lbs for ${
          chemicalForIncoming ? chemicalForIncoming.name : 'Unknown Chemical'
        }`
      );
    } catch (err) {
      console.error('Error adding incoming item:', err);
      showNotification('Could not add incoming item. Please try again.', 'error');
    }
  };

  const handleDeleteIncomingDrum = async (incomingId) => {
    const incomingDrumToDelete = incomingDrums.find(item => item.id === incomingId);
  
    const confirmed = window.confirm('Are you sure you want to delete this incoming item?');
  
    if (!confirmed) return;
  
    try {
      const response = await fetch(`https://crtapp.xyz/api/incoming-drums/${incomingId}`, {
        method: 'DELETE',
        cache: 'no-store',
        credentials: 'include'
      });
  
      if (!response.ok) {
        let message = 'Failed to delete incoming item.';
  
        try {
          const data = await response.json();
          message = data.message || message;
        } catch {
          message = `Failed to delete incoming item. Server returned ${response.status}.`;
        }
  
        showNotification(message, 'error');
        return;
      }
  
      await fetchIncomingDrums();
  
      showNotification('Incoming item deleted successfully.', 'success');
  
      logActivity(
        `Deleted incoming item ${
          incomingDrumToDelete
            ? (incomingDrumToDelete.tracking_number
                ? `(Tracking: ${incomingDrumToDelete.tracking_number})`
                : `ID: ${incomingId}`)
            : incomingId
        }`
      );
    } catch (err) {
      console.error('Error deleting incoming item:', err);
      showNotification('Could not delete incoming item. Please try again.', 'error');
    }
  };
  
  const handleMoveIncomingDrum = async (incomingId, incomingWeight, chemicalName) => {
    const confirmedWeight = window.prompt(
      `The expected weight is ${incomingWeight} lbs.\nPlease enter the actual received weight:`
    );
  
    const parsedWeight = parseFloat(confirmedWeight);
  
    if (
      confirmedWeight === null ||
      Number.isNaN(parsedWeight) ||
      parsedWeight <= 0
    ) {
      showNotification('Invalid weight entered. Operation cancelled.', 'error');
      return;
    }
  
    const lotNumber = window.prompt('Enter the chemical lot number for this item:');
  
    if (lotNumber === null) return;
  
    try {
      const response = await fetch(`https://crtapp.xyz/api/incoming-drums/${incomingId}/move`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          lot_number: lotNumber.trim(),
          confirmed_weight: parsedWeight
        })
      });
  
      if (!response.ok) {
        let message = 'Failed to move incoming item.';
  
        try {
          const data = await response.json();
          message = data.message || message;
        } catch {
          message = `Failed to move incoming item. Server returned ${response.status}.`;
        }
  
        showNotification(message, 'error');
        return;
      }
  
      await fetchIncomingDrums();
      await fetchChemicals();
  
      if (selectedChemical) {
        await fetchDrums(selectedChemical);
      }
  
      showNotification('Incoming item moved to inventory.', 'success');
  
      logActivity(
        `${parsedWeight} lbs of ${chemicalName} (Lot: ${lotNumber || 'N/A'}) added to inventory.`
      );
    } catch (err) {
      console.error('Error moving incoming item:', err);
      showNotification('Could not move incoming item. Please try again.', 'error');
    }
  };
  
  return (
    <div className="team-lead-dashboard">
      {notification && (
        <div className={`toast ${notification.type}`}>
          {notification.message}
        </div>
      )}
  
      <header className="dashboard-header">
        <nav>
          <ul className="header-nav">
            <li><Link to="/team-lead">Team Lead Dashboard</Link></li>
            <li><a href="#manage-chemicals">Manage Chemicals</a></li>
            {user && user.role?.toLowerCase() === 'team_lead' && (
              <li><Link to="/admin">Admin Center</Link></li>
            )}
          </ul>
        </nav>
      </header>
  
      <h1>Team Lead Dashboard</h1>
      <br />
      {error && <p className="error">{error}</p>}
      <Logout />
      <br />
  
      <div className="search-container">
        <input
          type="text"
          placeholder="Search chemical codes or locations..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          aria-label="Search chemicals"
        />
      </div>

      <div className="summary-cards">
  <div className="summary-card">
    <span className="summary-number">{dashboardStats.chemicalCount}</span>
    <span className="summary-label">Chemical Codes</span>
  </div>

  <div className="summary-card">
    <span className="summary-number">{dashboardStats.inventoryItemCount}</span>
    <span className="summary-label">Selected Items</span>
  </div>

  <div className="summary-card">
    <span className="summary-number">{dashboardStats.incomingItemCount}</span>
    <span className="summary-label">Incoming Items</span>
  </div>

  <div className="summary-card warning">
    <span className="summary-number">{dashboardStats.lowStockCount}</span>
    <span className="summary-label">Low Stock</span>
  </div>
</div>

      
      <div className="dashboard-content">
        <div id="chemicals-section" className="sidebar">
          <h2>Inventory</h2>
  
          {isLoading ? (
            <div className="spinner">Loading...</div>
          ) : (
            filteredChemicals.length > 0 ? (
              <ul className="chemical-list">
                {filteredChemicals.map((chemical) => (
                  <li
                  key={chemical.id}
                  onClick={() => {
                    console.log("Selected Chemical:", chemical);
                    setSelectedChemical(chemical);
                    fetchDrums(chemical);
                  }}
                  className={`chemical-item ${
                    selectedChemical?.id === chemical.id ? 'selected' : ''
                  } ${
                    chemical.threshold !== null &&
                    chemical.threshold !== undefined &&
                    chemical.total_weight < chemical.threshold
                      ? 'low-stock-item'
                      : ''
                  }`}
                >
                    <div>
                    <strong>
                    {chemical.name}
                    {chemical.threshold !== null &&
                      chemical.threshold !== undefined &&
                      chemical.total_weight < chemical.threshold &&
                      ' ⚠️'}
                  </strong>
                      <br />
                      <small>
                        📍 {chemical.location || 'Location not assigned'}
                        <br />
                        ⚖️ {chemical.total_weight} lbs
                      </small>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="empty-message">No chemicals found.</p>
            )
          )}
        </div>
  
        <div id="details-section" className="details-panel">
          {selectedChemical ? (
            <>
              <div className="card drum-management">
                <h2 className="selected-chemical-header">
                  Working on: {selectedChemical?.name}
                </h2>
  
                <div className="card location-section">
                  <h3>Location</h3>
  
                  <input
                    type="text"
                    placeholder="Enter location, shelf, fire cabinet, pallet, etc."
                    value={locationInput}
                    onChange={(e) => setLocationInput(e.target.value)}
                    aria-label="Chemical location"
                  />
  
                  <Button onClick={() => updateLocationForChemical(selectedChemical?.id, locationInput)}>
                    Update Location
                  </Button>
                </div>
  
                <h2>Inventory Items</h2>
  
                <table className="drum-list">
                  <thead>
                    <tr>
                      <th>Type</th>
                      <th>Identifier / Lot#</th>
                      <th>Current Weight</th>
                      <th>New Weight</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
  
                  <tbody>
                    {drums.map((drum) => (
                      <tr key={drum.id}>
                        <td>{drum.container_type || 'Drum'}</td>
                        <td> {drum.lot_number || `No Lot # (ID: ${drum.id})`} </td>
                        <td>{drum.weight} lbs</td>
                        <td>
                          <input
                            type="number"
                            placeholder="New weight"
                            value={newWeights[drum.id] || ''}
                            onChange={(e) =>
                              setNewWeights({ ...newWeights, [drum.id]: e.target.value })
                            }
                          />
                        </td>
                        <td>
                          <Button onClick={() => handleEditWeight(drum.id, newWeights[drum.id])}>
                            {loadingDrumId === drum.id ? 'Updating...' : 'Update'}
                          </Button>
  
                          {successMessages[drum.id] && (
                            <span className="success-message">{successMessages[drum.id]}</span>
                          )}
  
                          <Button onClick={() => handleDeleteDrum(drum.id)} className="delete-btn">
                            Delete
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                              <h3>
                Add Inventory Item: to {selectedChemical ? selectedChemical.name : 'Select a chemical'}
              </h3>

              <select
                value={containerType}
                onChange={(e) => setContainerType(e.target.value)}
                aria-label="Container type"
              >
                <option value="Drum">Drum</option>

                <option value="Pail">Pail</option>

                <option value="Bag">Bag</option>

                <option value="Box">Box</option>

                <option value="Jug">Jug</option>

                <option value="Bottle">Bottle</option>

                <option value="Case">Case</option>

                <option value="Tote">Tote</option>
              </select>

              <input
                type="number"
                placeholder="Enter item weight"
                value={newDrumWeight}
                min="0"
                step="0.01"
                aria-label="Inventory item weight"
                onChange={(e) => setNewDrumWeight(e.target.value)}
              />

              <input
                type="text"
                placeholder="Enter identifier / lot number"
                value={newLotNumber}
                aria-label="Inventory item identifier or lot number"
                onChange={(e) => setNewLotNumber(e.target.value)}
              />

              <Button onClick={handleAddDrum}>Add Inventory Item</Button>
            </div>

            <div className="card threshold-section">
              <h3>
                Set Threshold for: {selectedChemical?.name || 'Select a chemical'}{' '}
                (Current Threshold: {selectedChemical?.threshold !== undefined ? selectedChemical.threshold : 'N/A'} lbs)
              </h3>

              <input
                type="number"
                placeholder="Enter threshold weight"
                value={selectedThreshold}
                onChange={(e) => setSelectedThreshold(e.target.value)}
                aria-label="Threshold weight"
              />

              <Button onClick={() => updateThresholdForChemical(selectedChemical?.id, selectedThreshold)}>
                Update Threshold
              </Button>
            </div>

            <div className="card danger-zone">
              <h3>Danger Zone</h3>
              <p>
                This will permanently delete this chemical code if it has no inventory items or incoming items.
              </p>

              <Button
                className="delete-btn"
                onClick={() => handleDeleteChemical(selectedChemical.id)}
              >
                Delete {selectedChemical.name}
              </Button>
            </div>
          </>
        ) : (
          <p>Please select a chemical to view its details.</p>
        )}
      </div>
    </div>

    <div id="incoming-section" className="card">
      <h2>Incoming Inventory</h2>

      <div className="form-group">
        <label>Select Chemical Code:</label>

        <input
          type="text"
          placeholder="Search chemical code or location..."
          value={incomingChemicalSearch}
          onChange={(e) => {
            setIncomingChemicalSearch(e.target.value);
            setIncomingChemicalId('');
          }}
          aria-label="Search chemical code for incoming item"
        />

        {incomingChemicalSearch && incomingChemicalMatches.length > 0 && (
          <ul className="chemical-picker-results">
            {incomingChemicalMatches.map((chemical) => (
              <li
                key={chemical.id}
                onClick={() => {
                  setIncomingChemicalId(chemical.id);
                  setIncomingChemicalSearch(chemical.name);
                }}
              >
                <strong>{chemical.name}</strong>
                <span>{chemical.location || 'Location not assigned'}</span>
              </li>
            ))}
          </ul>
        )}

        {incomingChemicalSearch && incomingChemicalMatches.length === 0 && !incomingChemicalId && (
          <p className="empty-message">No matching chemical codes found.</p>
        )}

        {incomingChemicalId && (
          <p className="selected-picker-item">
            Selected: {incomingChemicalSearch}
          </p>
        )}
      </div>

      <div className="form-group">
        <input
          type="number"
          placeholder="Enter incoming weight"
          value={newIncomingWeight}
          min="0"
          step="0.01"
          aria-label="Incoming item weight"
          onChange={(e) => setNewIncomingWeight(e.target.value)}
        />

        <input
          type="text"
          placeholder="Enter tracking number (optional)"
          value={newIncomingTracking}
          aria-label="Tracking number"
          onChange={(e) => setNewIncomingTracking(e.target.value)}
        />

        <Button onClick={handleAddIncomingDrum}>Add Incoming Item</Button>
      </div>

      <div>
        <h3>Incoming Inventory List</h3>

        <ul className="incoming-drum-list">
          {incomingDrums.length > 0 ? (
            incomingDrums.map((incoming) => (
              <li key={incoming.id}>
                <span>
                  <strong>{incoming.chemical_name}</strong> | Weight: {incoming.weight} lbs | Tracking: {incoming.tracking_number || 'N/A'}
                </span>

                <Button onClick={() => handleDeleteIncomingDrum(incoming.id)}>
                  Delete
                </Button>

                <Button onClick={() => handleMoveIncomingDrum(incoming.id, incoming.weight, incoming.chemical_name)}>
                  Move to Inventory
                </Button>
              </li>
            ))
          ) : (
            <p className="empty-message">No incoming inventory items.</p>
          )}
        </ul>
      </div>
    </div>

    <div id="manage-chemicals" className="card">
      <h2>Manage Chemicals</h2>

      <div className="form-group">
        <h3>Add a New Chemical Code</h3>

        <input
          type="text"
          placeholder="Chemical Code"
          value={newChemicalName}
          onChange={(e) => setNewChemicalName(e.target.value)}
          aria-label="Chemical Code"
        />

        {chemicalInputError && (
          <span className="input-error">{chemicalInputError}</span>
        )}

        <Button onClick={handleAddChemical}>Add Chemical Code</Button>
      </div>
    </div>
    
    <div className="card reports-card">
  <h2>Reports</h2>

  <p>Download inventory reports for Excel.</p>

  <div className="reports-actions">
    <Button onClick={exportAllInventoryCSV}>
      Export All Inventory
    </Button>

    <Button onClick={exportLowStockCSV}>
      Export Low Stock
    </Button>

    <Button onClick={exportIncomingInventoryCSV}>
      Export Incoming Inventory
    </Button>
  </div>
</div>

    <div id="activity-log" className="card">
      <h2>Activity Log</h2>

      <ul className="activity-log-list">
        {activityLog.length > 0 ? (
          activityLog.map((entry, index) => (
            <li key={index}>
              <span className="log-time">[{entry.timestamp}]</span> {entry.message}
            </li>
          ))
        ) : (
          <p>No recent activity.</p>
        )}
      </ul>
    </div>
  </div>
);
}

export default TeamLeadDashboard;